"use client";

import { useState } from "react";
import { ChevronDown, ChevronUp, Activity } from "lucide-react";
import { serviceSnapshotRows } from "@/lib/service-snapshot";

/** Row label and value style per attribute (template order lives in SERVICE_SNAPSHOT_DEFAULTS). */
const ROW_STYLE: Record<string, { label: string; className: string }> = {
    core_identity: { label: "Service Type", className: "text-white font-semibold" },
    market_position: { label: "Best Known For", className: "text-white font-semibold" },
    user_level: { label: "Ideal For", className: "text-white font-semibold" },
    platform_tech: { label: "Technology", className: "text-white font-semibold" },
    killer_feature: { label: "Top Feature", className: "text-purple-400 font-bold" },
    financial_hook: { label: "Pricing / Guarantee", className: "text-white font-semibold" },
    product_list: { label: "Main Offerings", className: "text-white font-semibold" },
    support_type: { label: "Customer Support", className: "text-[#00ff9d] font-bold" },
};

/** REFERRAL pages (lib/page-type): the same box and table with their own title, subtitle and labelled rows. */
export interface SnapshotOverride {
    title: string;
    subtitle: string;
    rows: Array<{ key: string; label: string; value: string }>;
}

export default function TechnicalSpecs({ attributes, override }: { attributes: any; override?: SnapshotOverride }) {
    const [isExpanded, setIsExpanded] = useState(false);

    // Only rows with a real value; fewer than two real rows = no box (SEO plan 2026-10-24 R2.3).
    const rows: Array<{ key: string; value: string; label?: string }> = override ? override.rows : serviceSnapshotRows(attributes);
    if (rows.length === 0) return null;
    const labelOf = (row: { key: string; label?: string }) => row.label ?? ROW_STYLE[row.key].label;
    const classOf = (row: { key: string; label?: string }) => (row.label ? "text-white font-semibold" : ROW_STYLE[row.key].className);

    return (
        <div className="mb-8 rounded-xl border border-white/10 bg-[#1a1a1a]/50 overflow-hidden">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-white/5 transition-colors"
                aria-expanded={isExpanded}
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                        <Activity className="w-5 h-5" />
                    </div>
                    <div>
                        <p className="font-bold text-white text-sm">{override?.title ?? "Service Snapshot"}</p>
                        <p className="text-xs text-gray-400">{override?.subtitle ?? "Service Type, Technology & Guarantees"}</p>
                    </div>
                </div>
                {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {isExpanded && (
                <div className="border-t border-white/10">
                    <table className="w-full text-sm text-left">
                        <tbody className="divide-y divide-white/10 bg-black/20">
                            {rows.map((row, index) => (
                                <tr key={row.key}>
                                    <td className={`px-6 py-4 font-medium text-gray-400${index === 0 ? " w-1/3" : ""}`}>{labelOf(row)}</td>
                                    <td className={`px-6 py-4 ${classOf(row)}`}>{row.value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
